import { createContextApi, invalidatesOne, providesOne, CACHE_POLICY, providesList, createCursorPagedEndpoint, registerCrossContextInvalidation } from '@quickbee/store';
import { getContextClient } from '@quickbee/api-client';

// src/index.ts
var HELPDESK_TAGS = [
  "Ticket",
  "Agent",
  "AgentWorkload",
  "CalendarEvent",
  "SlaDashboard",
  // The ticket thread. Its own tag, not folded into `Ticket`: a comment changes no ticket field,
  // so invalidating `Ticket` on every note would refetch the drawer's whole detail projection —
  // and the escalation exchange posts comments the console did not initiate.
  "TicketComment",
  // Help content. `Article` and `ArticleCollection` are separate tags because the
  // grouped Knowledge Base read depends on both: renaming a collection must
  // refetch it without invalidating every flat FAQ list in every other app.
  "Article",
  "ArticleCollection",
  // QuickBee's own desk. Its own tags rather than a reuse of `Ticket`, because a case is a
  // different resource in a different schema: a jeweller's ticket write must never invalidate the
  // platform queue, and — the half that would actually bite — QuickBee answering a case must not
  // refetch a jeweller's ticket list that the answer has not reached yet.
  "PlatformCase",
  "PlatformCaseComment"
];
var WORKLOAD_CURRENT = { type: "AgentWorkload", id: "CURRENT" };
var AGENT_ROSTER_POLL_MS = 6e4;
var AGENT_ROSTER_POLL_OPTIONS = { pollingInterval: AGENT_ROSTER_POLL_MS };
var CALENDAR_LIST = { type: "CalendarEvent", id: "LIST" };
var SLA_CURRENT = { type: "SlaDashboard", id: "CURRENT" };
var TICKET_LIST = { type: "Ticket", id: "LIST" };
var ARTICLE_LIST = { type: "Article", id: "LIST" };
var COLLECTION_LIST = { type: "ArticleCollection", id: "LIST" };
var helpdeskEndpoints = (build) => ({
  /**
   * Returns the *summary* projection, not {@link TicketResponse} — the list has
   * no SLA timer and no assignment history, so a row cannot stand in for a
   * detail fetch.
   */
  listTickets: build.query({
    query: (params) => ({ url: "/helpdesk/tickets", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Ticket", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /**
   * The same read, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listTickets} drops `cursor` from its cache key and appends every response into a
   * single entry, which is exactly right for a "load more" list and exactly wrong for a numbered
   * pager: paging forward would grow the rendered set instead of replacing it, and paging *back*
   * would re-fetch a cursor already walked and append its rows a second time. A register whose
   * page two shows pages one *and* two, under a footer reading `9–16 of 4,318`, looks like
   * working pagination from everywhere except the row count.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * *Previous* free — a page already walked is answered from cache rather than re-fetched. It is
   * what `useCursorPager` reads; see `api-lender`'s `listLendersPage` and `api-audit`'s
   * `listEntries`/`listEntriesPage`, where the same pair exists for the same reason.
   *
   * `q` is a **server** filter on this endpoint, so a grid over this twin can send its search
   * box to the service rather than matching the page it already holds.
   */
  listTicketsPage: build.query({
    query: (params) => ({ url: "/helpdesk/tickets", params }),
    providesTags: (result) => providesList("Ticket", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getTicket: build.query({
    query: (id) => ({ url: `/helpdesk/tickets/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Ticket", id)
  }),
  /**
   * A self-service caller must send a non-blank `description` (the server
   * enforces it for own-scoped callers only) and its `customerName`/`assigneeId`
   * are ignored — requester identity comes from the `customer_id` claim (ADR-001).
   */
  createTicket: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/helpdesk/tickets",
      method: "POST",
      body,
      idempotencyKey: `ticket-create:${draftId}`
    }),
    // A new ticket moves the open count, can arrive pre-assigned, and puts a new
    // due-date on the calendar feed.
    invalidatesTags: [TICKET_LIST, WORKLOAD_CURRENT, CALENDAR_LIST, SLA_CURRENT]
  }),
  /**
   * Full replacement of the editable fields — `subject`, `channel` and
   * `priority` are all required server-side, so send the whole edit form rather
   * than the changed fields.
   */
  updateTicket: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/tickets/${id}`,
      method: "PATCH",
      body,
      idempotencyKey: `ticket-update:${id}:${requestId}`
    }),
    // Priority is editable here, and the SLA widget counts breaches by priority.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      SLA_CURRENT
    ]
  }),
  /** Assign or reassign. There is no unassign — the endpoint requires an `agentId`. */
  updateAssignee: build.mutation({
    query: ({ id, agentId, requestId }) => ({
      url: `/helpdesk/tickets/${id}/assignee`,
      method: "PATCH",
      body: { agentId },
      idempotencyKey: `ticket-assign:${id}:${requestId}`
    }),
    // The optimistic patch lives in index.ts (`enhanceEndpoints`), because it
    // needs a reference to the slice these endpoints are used to build.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT
    ]
  }),
  /**
   * Only `PENDING` and `WAITING_ON_CUSTOMER` are reachable here — resolution goes
   * through `closeTicket`, `CLOSED` is sweep-only (ADR-003), and `OPEN` is not
   * directly settable. `SettableTicketStatus` makes the other three untypeable.
   */
  updateStatus: build.mutation({
    query: ({ id, targetStatus, requestId }) => ({
      url: `/helpdesk/tickets/${id}/status`,
      method: "PATCH",
      body: { targetStatus },
      idempotencyKey: `ticket-status:${id}:${requestId}`
    }),
    // WAITING_ON_CUSTOMER pauses the SLA timer and moves the headline counts.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      SLA_CURRENT
    ]
  }),
  /**
   * The console's "Close Ticket" action, which *resolves* the ticket (ADR-003) —
   * permanent close is the sweep's job. Takes no body: there is no resolution
   * note on this endpoint.
   */
  closeTicket: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/helpdesk/tickets/${id}/close`,
      method: "POST",
      idempotencyKey: `ticket-close:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT,
      CALENDAR_LIST,
      SLA_CURRENT
    ]
  }),
  /** `newPriority` must be strictly more urgent than the current one (INV-15). */
  escalateTicket: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/tickets/${id}/escalate`,
      method: "POST",
      body,
      idempotencyKey: `ticket-escalate:${id}:${requestId}`
    }),
    // WORKLOAD_CURRENT because escalation recomputes the deadlines the roster's breaching
    // column reports. Correct to omit until the roster existed; wrong the moment it shipped.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT,
      SLA_CURRENT
    ]
  }),
  /**
   * Files the ticket as the tenant's own business or the platform's — the gate every platform
   * escalation has to pass. Held by `SUPPORT` as well as `SUPPORT_LEAD`: deciding whose problem a
   * ticket is is an agent's judgement; handing it to QuickBee is not.
   *
   * Frozen once escalated: the twin already describes the ticket as `TECHNICAL` and no leg of the
   * exchange carries a correction, so the server answers `TICKET_NOT_ESCALATABLE_TO_PLATFORM`.
   */
  classifyTicket: build.mutation({
    query: ({ id, classification, requestId }) => ({
      url: `/helpdesk/tickets/${id}/classification`,
      method: "PATCH",
      body: { classification },
      idempotencyKey: `ticket-classify:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Ticket", id)
  }),
  /**
   * Hands a `TECHNICAL` ticket to QuickBee's own support desk. **A different verb from
   * `escalateTicket`**, which is the priority bump and changes no status, no desk and no owner.
   *
   * The ticket moves to `WAITING_ON_PLATFORM` with its SLA clock paused, and refuses
   * edit/assign/status/close until QuickBee answers — so every headline count and the roster move
   * with it, hence the three invalidations. `SUPPORT_LEAD` only.
   */
  escalateToPlatform: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/tickets/${id}/escalate-platform`,
      method: "POST",
      body,
      idempotencyKey: `ticket-escalate-platform:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT,
      CALENDAR_LIST,
      SLA_CURRENT
    ]
  }),
  /**
   * **The outage path** (divergence D2). Raises a `TECHNICAL` ticket on this tenant's own desk AND
   * hands it to QuickBee in one act, for a problem with no customer ticket behind it.
   *
   * A different verb from `escalateToPlatform`, which needs a ticket to already exist. Everything
   * else is the same: the ticket comes back in `WAITING_ON_PLATFORM` with its clock paused and
   * refuses edit/assign/status/close until QuickBee answers. `SUPPORT_LEAD` only — the same grant,
   * because it is the same irreversible act of handing work across a tenant boundary.
   *
   * Invalidates the whole ticket LIST as well as the headline counts: this creates a row, so a
   * queue that only refreshed one id would not show it.
   */
  reportOutage: build.mutation({
    query: ({ requestId, ...body }) => ({
      url: "/helpdesk/tickets/outage-reports",
      method: "POST",
      body,
      idempotencyKey: `ticket-report-outage:${requestId}`
    }),
    invalidatesTags: () => [
      TICKET_LIST,
      WORKLOAD_CURRENT,
      CALENDAR_LIST,
      SLA_CURRENT
    ]
  }),
  /**
   * One ticket's thread, newest first.
   *
   * A requester sees `PUBLIC` comments alone — filtered server-side, so the page is a full page of
   * what they may see rather than a short one whose length counted the notes they may not.
   */
  listTicketComments: build.query({
    query: ({ ticketId, ...params }) => ({
      url: `/helpdesk/tickets/${ticketId}/comments`,
      params
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (_r, _e, { ticketId }) => [
      { type: "TicketComment", id: ticketId }
    ]
  }),
  /**
   * Posts one comment.
   *
   * `audience` defaults to `PUBLIC`. A requester's comment is forced to `PUBLIC` server-side
   * whatever is sent, so there is no need to hide the control from them — but do not offer them a
   * choice that will be ignored. `PLATFORM` requires a ticket this tenant has escalated to
   * QuickBee; on any other ticket the server answers `TICKET_COMMENT_INVALID`. (It used to also
   * accept QuickBee's *twin* of somebody else's ticket — the twin is gone, and QuickBee's side of
   * the thread is `postPlatformCaseComment` below.)
   *
   * **Usable while the ticket is `WAITING_ON_PLATFORM`**, unlike every other write in this
   * context — that is the whole point of the `PLATFORM` audience, and the window in which the two
   * desks most need to talk.
   */
  postTicketComment: build.mutation({
    query: ({ ticketId, requestId, ...body }) => ({
      url: `/helpdesk/tickets/${ticketId}/comments`,
      method: "POST",
      body,
      idempotencyKey: `ticket-comment:${ticketId}:${requestId}`
    }),
    // The thread only. A comment changes no ticket field, so refetching the detail projection
    // would be work for nothing.
    invalidatesTags: (_r, _e, { ticketId }) => [
      { type: "TicketComment", id: ticketId }
    ]
  }),
  /** Reopens a `RESOLVED` ticket (INV-12). Takes no body. */
  reopenTicket: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/helpdesk/tickets/${id}/reopen`,
      method: "POST",
      idempotencyKey: `ticket-reopen:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Ticket", id),
      WORKLOAD_CURRENT,
      CALENDAR_LIST,
      SLA_CURRENT
    ]
  }),
  /**
   * The requester's one-time CSAT score (INV-16). Keyed on the ticket id alone —
   * see the file header for why this one is deliberately not `requestId`-keyed.
   */
  rateSatisfaction: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/helpdesk/tickets/${id}/satisfaction`,
      method: "POST",
      body,
      idempotencyKey: `ticket-csat:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Ticket", id)
  }),
  /**
   * @deprecated superseded by {@link listSupportAgents}. Kept for one release so a stale MFE
   * bundle does not 404; no app imports the hook.
   */
  getAgentWorkload: build.query({
    query: () => ({ url: "/helpdesk/tickets/agent-workload" }),
    providesTags: [WORKLOAD_CURRENT],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /**
   * The Agents page's roster — who the desk's support agents are, their branch postings, and
   * their open workload.
   *
   * **Provides the existing `AgentWorkload`/`CURRENT` tag rather than a new one.** `Agent` is the
   * near-static assignable roster and `AgentWorkload` is the derived counts; these are the same
   * derived counts, so every ticket write that already invalidates `WORKLOAD_CURRENT` moves this
   * page too, without a third tag and without an assignment refetching the dropdown as well.
   *
   * **Must be polled, and that is not belt-and-braces.** `breachingCount` is moved by the SLA
   * breach sweep, which is a scheduled server-side job firing no mutation the cache could
   * invalidate on. Without polling the page silently shows a stale breach count — the one number
   * a support desk must not get wrong. RTK Query takes `pollingInterval` on the hook rather than
   * here, so the cadence lives in {@link AGENT_ROSTER_POLL_OPTIONS}; the page spreads it and pairs
   * it with a visible "updated HH:MM" and a manual refresh, because a number that changes on its
   * own has to say when it last did.
   */
  listSupportAgents: build.query({
    query: () => ({ url: "/helpdesk/agents" }),
    providesTags: [WORKLOAD_CURRENT],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getSupportAgent: build.query({
    query: (agentId) => ({ url: `/helpdesk/agents/${agentId}` }),
    providesTags: [WORKLOAD_CURRENT],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getAssignableAgents: build.query({
    query: () => ({ url: "/helpdesk/assignable-agents" }),
    // Keyed on `agentId`: these rows carry no `id`, and the default key would
    // silently degrade the tag set to a bare LIST entry.
    providesTags: (result) => providesList("Agent", result, "agentId"),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** `from`/`to` are both required by the controller — an omitted bound is a 400. */
  getCalendarEvents: build.query({
    query: ({ from, to }) => ({ url: "/helpdesk/calendar-events", params: { from, to } }),
    // Rows have no identifier of their own, so the feed invalidates as a whole.
    providesTags: [CALENDAR_LIST],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getSlaStatus: build.query({
    query: () => ({ url: "/helpdesk/dashboard/sla-status" }),
    providesTags: [SLA_CURRENT],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  // ── Help content ───────────────────────────────────────────────────────────
  //
  // Cached as `reference`, not `transactionalList`: help content changes when an
  // author edits it, not when a customer transacts, so the aggressive refetch
  // cadence the ticket lists need would be pure waste on a Help page that is
  // opened and closed.
  /**
   * The flat FAQ list — what every app's Help page renders.
   *
   * Always pass `surface`. Omitting it returns every surface's FAQ, so a
   * portfolio Help page would silently show POS and admin answers too.
   */
  listArticles: build.query({
    query: (params) => ({ url: "/helpdesk/articles", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Article", result),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  getArticle: build.query({
    query: (id) => ({ url: `/helpdesk/articles/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Article", id),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * Collections with their articles nested — the Knowledge Base page, and any
   * Help page that wants its FAQ grouped rather than flat.
   *
   * Unpaged by design: a surface's help corpus is reference data in the tens.
   * `type` defaults to `FAQ` server-side, so the KB page must send
   * `KNOWLEDGE_BASE` explicitly or it will render the FAQ corpus instead.
   */
  getKnowledgeBase: build.query({
    query: ({ surface, type }) => ({
      url: "/helpdesk/knowledge-base",
      params: type ? { surface, type } : { surface }
    }),
    // Provides both tags: the response is collections *and* articles, so either
    // kind of edit has to be able to invalidate it.
    providesTags: [ARTICLE_LIST, COLLECTION_LIST],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** `draftId` scopes the idempotency key to one authoring session, per §I-idempotency. */
  createArticle: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/helpdesk/articles",
      method: "POST",
      body,
      idempotencyKey: `article-create:${draftId}`
    }),
    invalidatesTags: [ARTICLE_LIST, COLLECTION_LIST]
  }),
  /**
   * Full replacement — send the whole edit form, not the changed fields.
   *
   * The key carries `requestId`, not just `id`, because editing is repeatable:
   * the filter keys on `(tenantId, key)` alone with a 24-hour TTL, so an
   * id-only key would replay the *first* edit's response for every later edit
   * that day and the second correction to an article would silently no-op.
   */
  updateArticle: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/articles/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `article-update:${id}:${requestId}`
    }),
    // COLLECTION_LIST too: an update can move the article between collections,
    // which changes the grouped read on both sides of the move.
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Article", id),
      ARTICLE_LIST,
      COLLECTION_LIST
    ]
  }),
  /**
   * Publish (`PUBLISHED`), unpublish (`DRAFT`) or archive (`ARCHIVED`).
   *
   * `requestId` is mandatory here for the same reason as the edit, and more
   * acutely: publish → unpublish → publish is an ordinary authoring sequence,
   * and an id-only key would replay the first publish and leave the article
   * live while the UI reported it as taken down.
   */
  changeArticleStatus: build.mutation({
    query: ({ id, requestId, targetStatus }) => ({
      url: `/helpdesk/articles/${id}/status`,
      method: "PATCH",
      body: { targetStatus },
      idempotencyKey: `article-status:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Article", id),
      ARTICLE_LIST,
      COLLECTION_LIST
    ]
  }),
  /** Returns the new collection's id, not the collection — the server issues a bare `201`. */
  createCollection: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/helpdesk/knowledge-base/collections",
      method: "POST",
      body,
      idempotencyKey: `article-collection-create:${draftId}`
    }),
    invalidatesTags: [COLLECTION_LIST]
  }),
  /** Returns `204 No Content` — the refreshed row arrives through tag invalidation. */
  updateCollection: build.mutation({
    query: ({ id, requestId, ...body }) => ({
      url: `/helpdesk/knowledge-base/collections/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `article-collection-update:${id}:${requestId}`
    }),
    invalidatesTags: [COLLECTION_LIST, ARTICLE_LIST]
  }),
  /**
   * Archive or restore. `204 No Content`.
   *
   * Invalidates `ARTICLE_LIST` as well: archiving a collection hides its
   * articles from every reader query without touching their own statuses, so a
   * flat FAQ list that only watched `ArticleCollection` would keep serving them.
   */
  changeCollectionStatus: build.mutation({
    query: ({ id, requestId, targetStatus }) => ({
      url: `/helpdesk/knowledge-base/collections/${id}/status`,
      method: "PATCH",
      body: { targetStatus },
      idempotencyKey: `article-collection-status:${id}:${requestId}`
    }),
    invalidatesTags: [COLLECTION_LIST, ARTICLE_LIST]
  }),
  // ── The Platform Desk (V4-5 — PlatformCasesController) ──────────────────────
  //
  // QuickBee's own support queue: escalations jewellers have handed over, the cross-tenant thread,
  // routing and the verdict. Every path below is `/helpdesk/platform-cases/**`, and every one of
  // them is refused server-side for a caller not signed in under the platform tenant — the grants
  // (`helpdesk-platform-cases:read|comment|assign|resolve`) cannot carry that restriction on their
  // own, because they are held by `SUPPORT`, which is the same role name in every jeweller's
  // tenant. The console withholds the nav item for the same reason; neither is the boundary.
  /** `helpdesk-platform-cases:read:tenant`. The queue, oldest-escalated first. */
  listPlatformCases: build.query({
    query: (params) => ({
      url: "/helpdesk/platform-cases",
      params
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("PlatformCase", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /**
   * The same queue, paged **discretely**. See {@link listTicketsPage} for why the pair exists:
   * an accumulating entry cannot back a numbered pager, and the Platform Desk's queue is
   * numbered.
   */
  listPlatformCasesPage: build.query({
    query: (params) => ({
      url: "/helpdesk/platform-cases",
      params
    }),
    providesTags: (result) => providesList("PlatformCase", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /** `helpdesk-platform-cases:read:tenant`. One case, addressed by its own id. */
  getPlatformCase: build.query({
    query: (caseId) => ({ url: `/helpdesk/platform-cases/${caseId}` }),
    providesTags: (_r, _e, caseId) => providesOne("PlatformCase", caseId)
  }),
  /**
   * The cross-tenant dialogue, oldest first. A plain list, not paged — the server returns the
   * whole thread, and a case's thread is bounded by the exchange rather than by a customer.
   */
  listPlatformCaseComments: build.query({
    query: (caseId) => ({ url: `/helpdesk/platform-cases/${caseId}/comments` }),
    providesTags: (_r, _e, caseId) => [
      { type: "PlatformCaseComment", id: caseId }
    ]
  }),
  /**
   * `helpdesk-platform-cases:assign:tenant`. Routes a case, or unassigns it with a null
   * `assigneeId`.
   *
   * Keyed with a `requestId` and not on the case alone: routing is repeatable against one case —
   * A → B → A is ordinary triage — and the store replays a completed 2xx for a repeat key for 24h,
   * so a case-only key would serve the first assignment's body back for the second and leave the
   * queue showing an owner who never took it.
   */
  assignPlatformCase: build.mutation({
    query: ({ caseId, requestId, assigneeId, assigneeName }) => ({
      url: `/helpdesk/platform-cases/${caseId}/assign`,
      method: "POST",
      body: {
        assigneeId,
        // Ignored server-side when unassigning; sending it anyway would put a name on a row that
        // has no owner if the two ever disagreed.
        ...assigneeId !== null && assigneeName ? { assigneeName } : {}
      },
      idempotencyKey: `platform-case-assign:${caseId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { caseId }) => invalidatesOne("PlatformCase", caseId)
  }),
  /**
   * `helpdesk-platform-cases:comment:tenant`. A reply to the jeweller, which crosses to their own
   * ticket as a `PLATFORM`-audience comment.
   *
   * Carries a `requestId` for the same reason `postTicketComment` does: commenting is repeatable
   * against one case, and one key per composed note (reused across retries of *that* note) is what
   * keeps a retry a retry rather than a second line in the thread.
   *
   * Invalidates the case as well as the thread: the first reply stamps `firstRespondedAt`, which
   * is the SLA the queue colours its rows by.
   */
  postPlatformCaseComment: build.mutation({
    query: ({ caseId, requestId, body }) => ({
      url: `/helpdesk/platform-cases/${caseId}/comments`,
      method: "POST",
      body: { body },
      idempotencyKey: `platform-case-comment:${caseId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { caseId }) => [
      { type: "PlatformCaseComment", id: caseId },
      ...invalidatesOne("PlatformCase", caseId)
    ]
  }),
  /**
   * `helpdesk-platform-cases:resolve:tenant`. QuickBee's verdict — the only verb anywhere that
   * releases the jeweller's ticket from `WAITING_ON_PLATFORM`.
   *
   * **Keyed on the case alone, deliberately, and it is the one write on this desk that is.**
   * Resolution is one-way — the aggregate refuses a second verdict outright — so a repeat can only
   * ever be a repeat, and a case-stable key stops it re-executing. That is the same call
   * `rateSatisfaction` makes for CSAT, and the whole of what the key buys.
   *
   * ⚠ **It does not replay the first response, and this note used to claim it did.** See the file
   * header: the stored body is empty, so a sequential retry resolves this mutation to `null` (the
   * screen recovers only via the invalidation below), and a genuine double-click is concurrent, so
   * the second POST is answered **409 `IDEMPOTENT_REQUEST_IN_PROGRESS`** — the very 409 the old
   * wording said a case-stable key avoided. `PlatformCasePage.handleAnswer` `.unwrap()`s, so that
   * 409 leaves the drawer open reporting a failure on a verdict that did land.
   *
   * The key stays, because what makes a duplicate safe is the aggregate and not the filter, and a
   * `requestId` would spread the same two behaviours over two keys rather than remove them. Do not
   * copy this shape onto a write whose response cannot be recovered by a refetch.
   */
  resolvePlatformCase: build.mutation({
    query: ({ caseId, ...body }) => ({
      url: `/helpdesk/platform-cases/${caseId}/resolve`,
      method: "POST",
      body,
      idempotencyKey: `platform-case-resolve:${caseId}`
    }),
    invalidatesTags: (_r, _e, { caseId }) => invalidatesOne("PlatformCase", caseId)
  })
});

// src/types.ts
var TICKET_STATUSES = [
  "OPEN",
  "PENDING",
  "WAITING_ON_CUSTOMER",
  "WAITING_ON_PLATFORM",
  "RESOLVED",
  "CLOSED"
];
var TICKET_CLASSIFICATIONS = [
  "BUSINESS",
  "TECHNICAL"
];
var TICKET_PRIORITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
var TICKET_CHANNELS = ["CALL", "EMAIL", "QUICKBEE_APP"];
var SETTABLE_TICKET_STATUSES = [
  "PENDING",
  "WAITING_ON_CUSTOMER"
];
var PRIORITY_RANK = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3
};
function isEscalation(current, next) {
  return PRIORITY_RANK[next] < PRIORITY_RANK[current];
}
function escalationTargets(current) {
  return TICKET_PRIORITIES.filter((priority) => isEscalation(current, priority));
}
var TICKET_COMMENT_AUDIENCES = [
  "PUBLIC",
  "INTERNAL",
  "PLATFORM"
];
var HELPDESK_MAX_PAGE_SIZE = 100;
var PLATFORM_CASE_STATUSES = [
  "OPEN",
  "PENDING",
  "RESOLVED",
  "CLOSED"
];
function client() {
  return getContextClient({ context: "helpdesk" });
}
async function fetchPublishedFaq(args = {}) {
  const { signal, ...params } = args;
  return client().get("/helpdesk/articles", {
    params: { ...params, type: "FAQ", status: "PUBLISHED" },
    ...signal ? { signal } : {}
  });
}
async function createSupportTicket(args) {
  const { draftId, ...body } = args;
  return client().post("/helpdesk/tickets", body, {
    idempotencyKey: `ticket-create:${draftId}`,
    retryable: true
  });
}

// src/index.ts
var baseHelpdeskApi = createContextApi({
  context: "helpdesk",
  reducerPath: "helpdeskApi",
  tagTypes: HELPDESK_TAGS,
  endpoints: helpdeskEndpoints
});
var helpdeskApi = baseHelpdeskApi.enhanceEndpoints({
  endpoints: {
    /**
     * `currentAssignee` is an object, not an id, so an honest optimistic patch
     * needs the agent's display name too. It is read from the already-cached
     * assignable-agents list; when that cache is cold (or the agent is not in
     * it), the patch is skipped entirely rather than rendering a placeholder
     * name that would flicker to the real one a moment later. The `invalidatesTags`
     * refetch still corrects the row either way.
     */
    updateAssignee: {
      async onQueryStarted({ id, agentId }, { dispatch, getState, queryFulfilled }) {
        const agents = baseHelpdeskApi.endpoints.getAssignableAgents.select()(
          getState()
        ).data;
        const agent = agents?.find((candidate) => candidate.agentId === agentId);
        if (!agent) {
          queryFulfilled.catch(() => void 0);
          return;
        }
        const assignment = {
          agentId,
          agentName: agent.displayName,
          assignedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
        const patch = dispatch(
          baseHelpdeskApi.util.updateQueryData("getTicket", id, (draft) => {
            draft.currentAssignee = assignment;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    },
    updateStatus: {
      async onQueryStarted({ id, targetStatus }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          baseHelpdeskApi.util.updateQueryData("getTicket", id, (draft) => {
            draft.status = targetStatus;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      }
    }
  }
});
var HELPDESK_INVALIDATION_RULES = [
  {
    event: "ticket.changed",
    // The list and the SLA widget are invalidated alongside the ticket itself:
    // the announced change may be a ticket this client has never fetched (the
    // `DisputeRaised` auto-creation path), which a detail-only tag would miss.
    invalidate: ({ ticketId }) => ticketId ? [
      { type: "Ticket", id: ticketId },
      { type: "Ticket", id: "LIST" },
      { type: "SlaDashboard", id: "CURRENT" }
    ] : [],
    // Already a helpdesk-context fact when emitted — no outbox hop to wait for.
    settleDelayMs: 0
  }
];
function registerHelpdeskInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: helpdeskApi,
    rules: HELPDESK_INVALIDATION_RULES
  });
}
var {
  useListTicketsQuery,
  /**
   * The discrete twin of {@link useListTicketsQuery}, for a numbered pager.
   *
   * Which one a screen wants follows from the control it renders: a "load more" list wants the
   * accumulating entry, a pager with page numbers wants this one. Picking the wrong one is not a
   * type error and not a visible failure — it is a register whose page two silently contains page
   * one as well.
   */
  useListTicketsPageQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useUpdateAssigneeMutation,
  useUpdateStatusMutation,
  useCloseTicketMutation,
  useEscalateTicketMutation,
  useClassifyTicketMutation,
  useEscalateToPlatformMutation,
  // The outage path (D2) — create-and-escalate in one call, for a problem with no customer
  // ticket behind it. Same grant as escalateToPlatform; the jeweller still gets a real ticket.
  useReportOutageMutation,
  useListTicketCommentsQuery,
  usePostTicketCommentMutation,
  useReopenTicketMutation,
  useRateSatisfactionMutation,
  useGetAgentWorkloadQuery,
  useListSupportAgentsQuery,
  useGetSupportAgentQuery,
  useGetAssignableAgentsQuery,
  useGetCalendarEventsQuery,
  useGetSlaStatusQuery,
  // Help content
  useListArticlesQuery,
  useGetArticleQuery,
  useGetKnowledgeBaseQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useChangeArticleStatusMutation,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useChangeCollectionStatusMutation,
  // The Platform Desk — QuickBee's own queue. Reachable only from a session on the platform
  // tenant; every one of these 403s for anybody else, by design and in the controller.
  useListPlatformCasesQuery,
  /** The discrete twin, for the queue's numbered pager. See {@link useListTicketsPageQuery}. */
  useListPlatformCasesPageQuery,
  useGetPlatformCaseQuery,
  useListPlatformCaseCommentsQuery,
  useAssignPlatformCaseMutation,
  usePostPlatformCaseCommentMutation,
  useResolvePlatformCaseMutation
} = helpdeskApi;

export { AGENT_ROSTER_POLL_MS, AGENT_ROSTER_POLL_OPTIONS, HELPDESK_INVALIDATION_RULES, HELPDESK_MAX_PAGE_SIZE, HELPDESK_TAGS, PLATFORM_CASE_STATUSES, SETTABLE_TICKET_STATUSES, TICKET_CHANNELS, TICKET_CLASSIFICATIONS, TICKET_COMMENT_AUDIENCES, TICKET_PRIORITIES, TICKET_STATUSES, createSupportTicket, escalationTargets, fetchPublishedFaq, helpdeskApi, isEscalation, registerHelpdeskInvalidation, useAssignPlatformCaseMutation, useChangeArticleStatusMutation, useChangeCollectionStatusMutation, useClassifyTicketMutation, useCloseTicketMutation, useCreateArticleMutation, useCreateCollectionMutation, useCreateTicketMutation, useEscalateTicketMutation, useEscalateToPlatformMutation, useGetAgentWorkloadQuery, useGetArticleQuery, useGetAssignableAgentsQuery, useGetCalendarEventsQuery, useGetKnowledgeBaseQuery, useGetPlatformCaseQuery, useGetSlaStatusQuery, useGetSupportAgentQuery, useGetTicketQuery, useListArticlesQuery, useListPlatformCaseCommentsQuery, useListPlatformCasesPageQuery, useListPlatformCasesQuery, useListSupportAgentsQuery, useListTicketCommentsQuery, useListTicketsPageQuery, useListTicketsQuery, usePostPlatformCaseCommentMutation, usePostTicketCommentMutation, useRateSatisfactionMutation, useReopenTicketMutation, useReportOutageMutation, useResolvePlatformCaseMutation, useUpdateArticleMutation, useUpdateAssigneeMutation, useUpdateCollectionMutation, useUpdateStatusMutation, useUpdateTicketMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map